import { useCallback, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import { isValidSpeedString, parseSpeedString } from '../device/DeviceHelper';

/**
 * Dialog for entering a custom interface speed.
 * Accepts format: digits + optional K/M/G/T suffix (e.g., "40G", "100M", "500000").
 * Returns the parsed speed in bps via onSave callback.
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onClose - Close handler (cancel)
 * @param {Function} props.onSave - Save handler, receives speed in bps (number)
 */
const CustomSpeedDialog = ({ open, onClose, onSave }) => {
    const [speedInput, setSpeedInput] = useState('');

    const isValid = isValidSpeedString(speedInput);

    const handleChange = useCallback((event) => {
        setSpeedInput(event.target.value);
    }, []);

    const handleSave = useCallback(() => {
        const speedBps = parseSpeedString(speedInput);
        if (speedBps) {
            onSave(speedBps);
            setSpeedInput('');
        }
    }, [speedInput, onSave]);

    const handleClose = useCallback(() => {
        setSpeedInput('');
        onClose();
    }, [onClose]);

    return (
        <Dialog open={open} onClose={handleClose} aria-labelledby="custom-speed-dialog-title" maxWidth="xs" fullWidth>
            <DialogTitle id="custom-speed-dialog-title">
                <Trans>Custom Speed</Trans>
            </DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    fullWidth
                    margin="dense"
                    label={i18n._(t`Interface Speed (bps)`)}
                    placeholder={i18n._(t`e.g. 40G, 100M, 500000`)}
                    value={speedInput}
                    onChange={handleChange}
                    error={speedInput.length > 0 && !isValid}
                    helperText={speedInput.length > 0 && !isValid ? i18n._(t`Invalid speed format`) : ''}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button onClick={handleSave} color="primary" disabled={!isValid}>
                    <Trans>Save</Trans>
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CustomSpeedDialog;
